import StaffOrder from "@/modules/team/staffOrder";
import CustomDrawer from "@/components/drawer";
import Header from "@/components/header";
import ListItemLoading from "@/components/loading/listItemLoading/listItemLoading";
import TeamItem from "@/modules/team/list/item";
import SearchBox from "@/modules/team/list/searchBox";
import useGetMerchantUsers from "@/services/merchantUser/hooks/useGetMerchantUsers";
import { MerchantUserDto } from "@/models/club";

import {
  Anchor,
  Button,
  Container,
  Divider,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconCalendarEvent, IconCash, IconPlus, IconUsers, IconWorld } from "@tabler/icons-react";
import HelpButton from "@/modules/help/HelpButton";
import HelpPanel from "@/modules/help/HelpPanel";
import { teamMemberAddHelp } from "@/modules/help/topics/teamMemberAdd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import useIsSmall from "@/hooks/useIsSmall";

const TeamMembers = () => {
  const [search, setSearch] = useState<string>("");
  const { data, isLoading } = useGetMerchantUsers();
  const [selectedItem, setSelectedItem] = useState<MerchantUserDto>();
  const [opened, { open, close }] = useDisclosure(false);
  const isSmall = useIsSmall();
  const [helpOpened, setHelpOpened] = useState(false);
  const isEmpty = !isLoading && (data?.length ?? 0) === 0;
  const handleSearchChange = (e: string) => {
    if (e.length >= 3) {
      setSearch(e);
    } else if (e.length === 0) {
      setSearch("");
    }
  };
  const navigate = useNavigate();
  const menuItems = () => {
    return (
      <>
        <Menu.Item
          disabled={!selectedItem?.id}
          onClick={() => {
            if (!selectedItem?.id) return;
            close();
            navigate(`/dashboard/barber-overview?merchantUserId=${selectedItem.id}`);
          }}
        >
          <Text fw="500">آمار</Text>
        </Menu.Item>
        <Menu.Item onClick={() => { close(); navigate(`/dashboard/teams/teamMembers/detail/${selectedItem?.id}?tab=portfolio`); }}>
          <Text fw="500">بیوگرافی و نمونه‌کار</Text>
        </Menu.Item>
        <Menu.Item
          onClick={() =>
            navigate(`/dashboard/teams/teamMembers/edit/${selectedItem?.id}`)
          }
        >
          <Text fw="500">ویرایش اطلاعات شخصی</Text>
        </Menu.Item>
        <Menu.Item
          onClick={() =>
            navigate(
              `/dashboard/teams/teamMembers/editServices/${selectedItem?.id}`
            )
          }
        >
          <Text fw="500"> ویرایش خدمات</Text>
        </Menu.Item>
        <Menu.Item>
          <Text fw="500"> مشاهده تقویم کاری</Text>
        </Menu.Item>
        <Menu.Item>
          <Text fw="500" c="red">
            {" "}
            آرشیو
          </Text>
        </Menu.Item>
      </>
    );
  };
  return (
    <>
      {
        isSmall ? <Header
          title="تیم"
          showBack
          route="/dashboard/teams"
          customComponent={
            <Group gap="xs">
              <HelpButton active={helpOpened} onClick={() => setHelpOpened((opened) => !opened)} />
              <Button
                px="xs"
                variant="default"
                leftSection={<IconPlus size={14} />}
                onClick={() => navigate("add")}
              >
                افزودن
              </Button>
            </Group>
          }
        /> : null
      }

      {/* On wide screens the guide sits beside the list; on phones it is a bottom sheet. */}
      <Group align="flex-start" justify="center" wrap="nowrap" gap="lg" px={isSmall ? 0 : "md"}>
      <Container w="100%" size="md" bg="white" style={{ minWidth: 0 }}>
        <Group justify="space-between">
          <Title order={4}>تیم</Title>
          {
            !isSmall && <Group gap="xs">
              <HelpButton active={helpOpened} onClick={() => setHelpOpened((opened) => !opened)} />
              <Button
                px="xs"
                variant="default"
                leftSection={<IconPlus size={14} />}
                onClick={() => navigate("add")}
              >
                افزودن
              </Button>
            </Group>
          }

        </Group>

        {isEmpty && <EmptyTeam onAdd={() => navigate("add")} onHelp={() => setHelpOpened(true)} />}

        {!isEmpty && <Stack mt="md" justify="start" flex={1}>
          <SearchBox setSearch={handleSearchChange} />
          <StaffOrder />
          <Stack gap="lg" mb="lg">
            {isLoading && <ListItemLoading count={4} />}
            {data
              ?.filter(
                (team) =>
                  team.firstName?.includes(search) ||
                  team.lastName?.includes(search)
              )
              .map((team, index) => (
                <>
                  <TeamItem
                    key={team.id}
                    team={team}
                    setSelectedItem={setSelectedItem}
                    open={open}
                    menuItems={menuItems}
                    isSmall={isSmall}
                  />
                  {index !== data?.length - 1 && <Divider />}
                </>
              ))}
          </Stack>
        </Stack>}
      </Container>
      <HelpPanel topic={teamMemberAddHelp} opened={helpOpened} onClose={() => setHelpOpened(false)} />
      </Group>
      <CustomDrawer
        opened={opened && isSmall}
        onClose={close}
        title={`${selectedItem?.firstName} ${selectedItem?.lastName}`}
        close={close}
        position="bottom"
        radius="md"
        custom="team-member-setting"
        size="360"
      >
        <Menu>{menuItems()}</Menu>
      </CustomDrawer>
    </>
  );
};

export default TeamMembers;

const emptyTeamBenefits = [
  { icon: IconCalendarEvent, text: "در تقویم ستون خودش را دارد و برایش نوبت ثبت می‌شود" },
  { icon: IconWorld, text: "مشتری در سایت سالن، خدمات او را رزرو می‌کند" },
  { icon: IconCash, text: "سهمش از هر خدمت در «تسویه تیم» حساب می‌شود" },
];

function EmptyTeam({ onAdd, onHelp }: { onAdd: () => void; onHelp: () => void }) {
  return (
    <Stack align="center" gap="md" py="xl" maw={420} mx="auto">
      <ThemeIcon size={84} radius={26} variant="light">
        <IconUsers size={42} stroke={1.5} />
      </ThemeIcon>
      <Stack gap={6} ta="center">
        <Title order={4}>هنوز کسی به تیم اضافه نشده</Title>
        <Text size="sm" c="dimmed" lh={1.9}>
          هر کسی که در سالن خدمت می‌دهد را اینجا اضافه کن.
        </Text>
      </Stack>
      <Paper withBorder radius="lg" p="md" w="100%">
        <Stack gap="sm">
          <Text size="xs" fw={700} c="dimmed">
            بعد از افزودن هر عضو:
          </Text>
          {emptyTeamBenefits.map(({ icon: Icon, text }) => (
            <Group key={text} gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" radius="md" size={34}>
                <Icon size={18} />
              </ThemeIcon>
              <Text size="sm" lh={1.7}>
                {text}
              </Text>
            </Group>
          ))}
        </Stack>
      </Paper>
      <Button fullWidth size="md" leftSection={<IconPlus size={18} />} onClick={onAdd}>
        افزودن به تیم
      </Button>
      <Anchor component="button" type="button" size="sm" fw={700} onClick={onHelp}>
        این بخش چطور کار می‌کند؟
      </Anchor>
    </Stack>
  );
}
